import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  ArrowLeft, 
  Code, 
  Save,
  RefreshCw,
  Play,
  Database,
  FileCode,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ScriptsDelSistema = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [testVariables, setTestVariables] = useState({
    tdias: '0',
    tmin: '1',
    thoras: '0',
    plan_name: 'PRUEBA',
    validity_minutes: '1'
  });

  // Verificar que solo super admin pueda acceder
  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadScripts();
  }, []);

  const loadScripts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/superadmin/script-templates');
      setScripts(response.data);
    } catch (error) {
      console.error('Error loading scripts:', error);
      alert('Error cargando scripts: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const initializeScripts = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/superadmin/script-templates/init');
      alert(response.data.message);
      await loadScripts();
    } catch (error) {
      alert('Error inicializando scripts: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const selectScript = (script) => {
    setSelectedScript(script);
    setEditedContent(script.script_content);
    setTestResult('');
  };

  const saveScript = async () => {
    if (!selectedScript) return;
    
    setLoading(true);
    try {
      await api.put(`/api/superadmin/script-templates/${selectedScript.id}`, {
        script_content: editedContent
      });
      alert('✅ Script guardado exitosamente');
      await loadScripts();
      
      // Actualizar el script seleccionado
      const updatedScript = { ...selectedScript, script_content: editedContent };
      setSelectedScript(updatedScript);
    } catch (error) {
      alert('Error guardando script: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const testScript = async () => {
    if (!editedContent) return;
    
    setLoading(true);
    try {
      const response = await api.post('/api/superadmin/script-templates/test', {
        script_content: editedContent,
        variables: testVariables
      });
      setTestResult(response.data.result);
    } catch (error) {
      alert('Error probando script: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return null;
  }

  const getScriptColor = (script) => {
    if (script.version === 'v7') return 'bg-blue-50 border-blue-200';
    return 'bg-green-50 border-green-200';
  };

  const getTypeColor = (type) => {
    if (type === 'on_login') return 'text-purple-700 bg-purple-100';
    if (type === 'on_logout') return 'text-orange-700 bg-orange-100';
    return 'text-indigo-700 bg-indigo-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Panel Super Admin
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                📜 Scripts del Sistema
              </h1>
              <p className="text-gray-600">
                Gestión centralizada de scripts RouterOS para v6 y v7
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={loadScripts}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className="w-5 h-5" />
                Recargar
              </button>
              
              {scripts.length === 0 && (
                <button
                  onClick={initializeScripts}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  <Database className="w-5 h-5" />
                  Inicializar Scripts
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Scripts */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileCode className="w-6 h-6 text-indigo-600" />
                Templates Disponibles
              </h2>
              
              {scripts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Code className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay scripts configurados</p>
                  <p className="text-sm mt-2">Haz clic en "Inicializar Scripts"</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scripts.map((script) => (
                    <button
                      key={script.id}
                      onClick={() => selectScript(script)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        selectedScript?.id === script.id
                          ? 'border-purple-500 bg-purple-50'
                          : getScriptColor(script)
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-gray-900">
                          {script.version.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(script.script_type)}`}>
                          {script.script_type}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-700 mb-1">
                        {script.plan_type}
                      </div>
                      <div className="text-xs text-gray-500">
                        {script.description}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Editor de Script */}
          <div className="lg:col-span-2">
            {selectedScript ? (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Code className="w-6 h-6 text-purple-600" />
                    {selectedScript.name}
                  </h2>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={testScript}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                    >
                      <Play className="w-4 h-4" />
                      Probar
                    </button>
                    
                    <button
                      onClick={saveScript}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      Guardar
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Script Content
                  </label>
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm bg-gray-900 text-green-400"
                    spellCheck={false}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Variables disponibles: {selectedScript.variables?.join(', ') || 'Ninguna'}
                  </p>
                </div>

                {/* Variables de Prueba */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Play className="w-5 h-5 text-blue-600" />
                    Variables de Prueba
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.keys(testVariables).map((key) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {'{{'}{key}{'}}'} 
                        </label>
                        <input
                          type="text"
                          value={testVariables[key]}
                          onChange={(e) => setTestVariables({...testVariables, [key]: e.target.value})}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resultado de Prueba */}
                {testResult && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      Resultado de Prueba
                    </h3>
                    <pre className="text-green-400 text-sm overflow-auto max-h-64">
                      {testResult}
                    </pre>
                  </div>
                )}

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Información importante:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Usa {'{{'} y {'}}'}  para variables (ej: {'{{'} tdias {'}}'}  )</li>
                        <li>Los cambios afectan NUEVOS perfiles/usuarios creados</li>
                        <li>Prueba siempre antes de guardar cambios importantes</li>
                        <li>Mantén un backup del script original</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <Code className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Selecciona un script
                </h3>
                <p className="text-gray-600">
                  Elige un script de la lista para editarlo o probarlo
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptsDelSistema;
